'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useNumericId from '@repo/ui/src/hooks/useNumericId';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { type LocallyConfig } from '@repo/ui/src/components/LocallyDropdown';

export interface BuyOnlineButtonProps {
  upc: string;
  config: LocallyConfig;
  size?: 'small' | 'medium';
}

const BuyOnlineButtonInner = ({ upc, config, size = 'medium' }: BuyOnlineButtonProps) => {
  const instanceId = useNumericId();
  const { t } = useTranslation();
  const { geo } = useCurrentUrl();
  const lang = geo?.locale?.toLowerCase() || 'en-us';
  const isCompact = size === 'small';
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isLocallyLoaded, setIsLocallyLoaded] = React.useState(false);

  const generateConfig = React.useCallback(() => {
    const isCanada = geo?.geoLoc?.includes('ca');
    return {
      id: String(instanceId),
      company_name: config.companyName,
      button_text: t('productDetailPageContent.locallyButtonText', undefined, {
        default: 'Find it Locally',
      }),
      button_id: 'HTML',
      company_id: String(config.companyId),
      css: '3',
      color_0: '#D95E16',
      always_hide_button: '1',
      enable_buy_online_button: '1',
      buy_online_button_text: t('productDetailPageContent.buyOnlineButtonText', undefined, {
        default: 'Buy',
      }),
      n_buy_online_results: '3',
      upc,
      show_location_prompt: '1',
      lang,
      n_dealers: '3',
      no_link: '1',
      default_tab: '1',
      radius_in_miles: '25',
      ...(isCanada && { only_show_country: 'CA' }),
    };
  }, [instanceId, upc, lang, t, config.companyName, config.companyId, geo?.geoLoc]);

  const generateUrl = React.useCallback((cfg: Record<string, unknown>) => {
    return new URLSearchParams(
      Object.entries(cfg).map(([key, value]) => [
        key,
        typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? ''),
      ]),
    );
  }, []);

  const injectScript = React.useCallback((src: string, scriptId: string) => {
    const existingScript = document.getElementById(scriptId);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  React.useEffect(() => {
    const cfg = generateConfig();
    const url = generateUrl(cfg);
    const scriptId = `lcly-script-${instanceId}`;

    injectScript(`https://${config.domain}/stores/map.js?${url}`, scriptId);
    injectScript(
      `https://${config.domain}/switchlive/switchlive-events.js?company_id=${config.companyId}`,
      `lcly-script-switchlive-${instanceId}`,
    );
    injectScript(
      `https://${config.domain}/js/switchlive.js?company_id=${config.companyId}`,
      `lcly-script-switchlive-events-${instanceId}`,
    );
  }, [generateConfig, generateUrl, injectScript, upc, lang, instanceId, config]);

  React.useEffect(() => {
    const checkLocallyButton = () => {
      const locallyButton = containerRef.current?.querySelector<HTMLAnchorElement>(
        `#lcly-button-${instanceId} a[id^="lcly-buy-online-button"]`,
      );
      if (locallyButton) {
        setIsLocallyLoaded(true);
        return true;
      }
      return false;
    };

    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      if (checkLocallyButton()) {
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Bail out after 10 seconds in case the Locally script never injects the button
    // (e.g. network failure or ad blocker), so the observer doesn't run indefinitely.
    const timeoutId = setTimeout(() => observer.disconnect(), 10000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [instanceId]);

  const handleBuyOnlineClick = React.useCallback(() => {
    const locallyButton = containerRef.current?.querySelector<HTMLAnchorElement>(
      `#lcly-button-${instanceId} a[id^="lcly-buy-online-button"]`,
    );
    if (locallyButton) {
      locallyButton.click();
    }
  }, [instanceId]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'w-full shop-local-button-style-container [&>*]:!hidden [&>button]:!inline-flex',
        isCompact && 'shop-local-button-style--compact',
      )}
    >
      {isLocallyLoaded && (
        <Button
          onClick={handleBuyOnlineClick}
          variant={ButtonVariant.primary}
          size={isCompact ? 'small' : 'medium'}
          className="justify-center"
          full
        >
          {t('productDetailPageContent.buyOnlineButtonText', undefined, {
            default: 'Buy',
          })}
        </Button>
      )}
      <div
        id={`lcly-button-${instanceId}`}
        data-switchlive="true"
        data-switchlive-impression="true"
        data-switchlive-impression-id-PL="1"
        className={classNames(
          'shop-local-button-style hidden',
          isCompact && 'shop-local-button-style--compact',
        )}
      >
        <a
          id={`lcly-link-${instanceId}`}
          data-switchlive="true"
          data-switchlive-mode="auto"
          data-switchlive-id-PL="6"
          role="button"
          href="https://join.locally.com/for-brands"
          target="_blank"
          rel="noopener noreferrer"
        >
          Product Locator by Locally
        </a>
      </div>
    </div>
  );
};

const BuyOnlineButton = dynamic(() => Promise.resolve(BuyOnlineButtonInner), { ssr: false });

export default BuyOnlineButton;
